import { useEffect, useRef } from 'react';

import { useProgressRing } from '@/lib/motion';

/** Colores de las áreas del legacy (avances): Cirugía, Ginecología, Medicina Interna, Pediatría. */
const COLOR_AREA: Record<string, string> = {
  cirugia: '#0FD0CD',
  gineco: '#F37EA8',
  medint: '#5B7BB8',
  pedia: '#7E9DAF',
};

/**
 * Dona de aciertos (legacy: Chart.js con hueco del 80 %). Anillo SVG animado con anime.js;
 * el porcentaje va en texto HTML encima (sin el plugin global del legacy).
 */
export function Dona({
  percent,
  etiqueta,
  tam = 150,
}: {
  percent: number;
  etiqueta?: string;
  tam?: number;
}) {
  const ring = useRef<SVGCircleElement>(null);
  const r = 42;
  useProgressRing(ring, Math.max(0, Math.min(100, percent)));
  return (
    <div className="relative grid place-items-center" style={{ width: tam, height: tam }}>
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden>
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="rgb(127 140 160 / 0.22)"
          strokeWidth="9"
        />
        <circle
          ref={ring}
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="#1CD0C7"
          strokeWidth="9"
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute text-center">
        <p className="text-3xl font-extrabold tabular">{percent}%</p>
        {etiqueta && <p className="text-xs text-texto-3">{etiqueta}</p>}
      </div>
    </div>
  );
}

/** Barras verticales 0–100 % por área. */
export function Barras({ items }: { items: { key: string; label: string; value: number }[] }) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bars = root.current?.querySelectorAll<HTMLElement>('[data-barra]') ?? [];
    bars.forEach((b) => {
      b.style.transform = 'scaleY(1)';
    });
  }, [items]);
  return (
    <div
      ref={root}
      className="flex h-44 items-end gap-3"
      role="img"
      aria-label={items.map((i) => `${i.label}: ${String(i.value)} %`).join(', ')}
    >
      {items.map((i) => (
        <div key={i.key} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
          <span className="text-xs font-bold tabular">{i.value}%</span>
          <div className="flex w-full max-w-14 flex-1 items-end rounded-t-md bg-[rgb(127_140_160/0.15)]">
            <div
              data-barra
              className="w-full origin-bottom rounded-t-md transition-transform duration-700 ease-out motion-reduce:transition-none"
              style={{
                height: `${String(Math.max(i.value, 2))}%`,
                background: COLOR_AREA[i.key] ?? '#005DB9',
                transform: 'scaleY(0)',
              }}
            />
          </div>
          <span className="text-center text-[10px] font-bold text-texto-3 uppercase">
            {i.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Intentos por semana (legacy: línea con eje Y fijo en 30 e invertido). Aquí el eje Y se ajusta
 * al máximo y el tiempo corre de izquierda (hace 9 semanas) a derecha (esta semana).
 */
export function Linea({ semanas, etiqueta }: { semanas: number[]; etiqueta: string }) {
  const datos = [...semanas].reverse();
  const max = Math.max(1, ...datos);
  const w = 300;
  const h = 90;
  const paso = w / Math.max(datos.length - 1, 1);
  const puntos = datos.map((v, i) => [i * paso, h - (v / max) * (h - 10) - 5] as const);
  return (
    <figure>
      <svg
        viewBox={`-6 0 ${String(w + 12)} ${String(h + 18)}`}
        className="w-full"
        role="img"
        aria-label={`${etiqueta}: ${datos.join(', ')}`}
      >
        <line x1="0" y1={h - 5} x2={w} y2={h - 5} stroke="rgb(127 140 160 / 0.35)" />
        <polyline
          points={puntos.map((p) => p.join(',')).join(' ')}
          fill="none"
          stroke="#2E99DA"
          strokeWidth="2"
        />
        {puntos.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill="#2E99DA">
            <title>{`${String(datos[i])} intento(s)`}</title>
          </circle>
        ))}
        {datos.map((_, i) => (
          <text
            key={i}
            x={i * paso}
            y={h + 14}
            textAnchor="middle"
            fontSize="9"
            fill="currentColor"
            className="text-texto-3"
          >
            {i === datos.length - 1 ? 'Hoy' : `S-${String(datos.length - 1 - i)}`}
          </text>
        ))}
      </svg>
      <figcaption className="mt-1 text-center text-xs text-texto-3">
        {etiqueta} (S-1 = la semana pasada)
      </figcaption>
    </figure>
  );
}
